import { Component, computed, effect, ElementRef, HostListener, OnInit, Signal, signal, ViewChild, WritableSignal } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Section, WorkflowConfig } from 'classes-lib';
import { ClientConfigService } from 'client-config-service-lib';
import { Namespace, SectionType, Workflow } from 'enums-lib';
import { FeatureFlagService } from 'feature-flags-lib';
import { IEntityTypeCount, IIconService, IWorkflowConfig } from 'interfaces-lib';
import { MessageNotificationService } from 'message-notification-service-lib';
import { combineLatest, of } from 'rxjs';
import { catchError, take } from 'rxjs/operators';
import { SharedServiceProviderServiceFactory } from 'shared-service-provider-lib';
import { IdentityStorage } from 'storage-service-lib';
import { WorkflowConfigDataService } from '../../services/data/workflow-config-data.service';
import { EntityTypeServiceFactory } from '../../services/entity-type/entity-type-service.factory';
import { EntityTypeService, IEntityLabelPair } from '../../services/entity-type/entity-type.service';
import { UiService } from '../../services/ui.service';
import { WorkflowConfigService, WorkflowConfigUpdateEvent, WorkflowConfigUpdateHint } from '../../services/workflow-config.service';
import { Adv2ConfigComponent } from '../adv2-config/adv2-config.component';
import { WorkflowConfigCopyService } from '../workflow-config/config/workflow-config-copy.service';
import { WorkflowConfigComponent } from '../workflow-config/config/workflow-config.component';

type TableViewModel = { label: string; tooltip: string } & IWorkflowConfig;
interface ITableFilter {
  column: string;
  value: string;
}
interface ICombinedFilter {
  workflowType: ITableFilter;
  entityFilter: ITableFilter;
  columnFilter: ITableFilter[];
}

const BASE_COLUMNS = ['label', 'name', 'isPublic', 'dateCreated', 'createdBy', 'dateModified', 'modifiedBy', 'actions'];

@Component({
  selector: 'app-workflow-config-list-view',
  templateUrl: './workflow-config-list-view.component.html',
  styleUrls: ['./workflow-config-list-view.component.scss'],
  standalone: false,
})
export class WorkflowConfigListViewComponent implements OnInit {
  get currentWorkflowTemplateCount(): number {
    switch (this.workflowType) {
      case Workflow.SimpleDocx:
        return this.awesomeDocsCount;
      case Workflow.DocumentMerge:
        return this.docMergeCount;
      default:
        return 0;
    }
  }

  get namespace(): Namespace {
    return this.identityStorage.get().namespace;
  }

  dataSource: MatTableDataSource<TableViewModel>;
  workflowConfigs: IWorkflowConfig[] = [];
  entityTypesUsed: IEntityTypeCount[] = [];
  noResults = false;
  iconService: IIconService;
  workflowType: Workflow = Workflow.SimpleDocx;

  recordTerm = 'Item';

  awesomeDocsCount = 0;
  docMergeCount = 0;
  totalTemplates = 0;

  entityLabelPairList: IEntityLabelPair[] = [];

  datasourceInitiated: WritableSignal<boolean> = signal(false);
  columnFilter: WritableSignal<ITableFilter[]> = signal([]);
  entityFilter: WritableSignal<string> = signal('');
  workflowFilter: WritableSignal<string> = signal(Workflow.SimpleDocx);
  combinedTableFilter: Signal<ICombinedFilter> = computed(() => {
    return {
      columnFilter: this.columnFilter(),
      entityFilter: { column: 'entity', value: this.entityFilter() },
      workflowType: { column: 'workflow', value: this.workflowFilter() },
    };
  });

  private readonly attachmentColumnEnabled = this.featureFlagService.enabled(FeatureFlagService.FLAGS.templateListPaperclipIcon);

  // KAD2-2430 - enabled() is false until the flags (loaded asynchronously during auth) are ready; this
  // produces a new array reference once they settle so mat-table re-renders the columns.
  readonly displayedColumns: Signal<string[]> = computed(() => {
    const columns = [...BASE_COLUMNS];
    if (this.attachmentColumnEnabled()) {
      columns.splice(columns.indexOf('isPublic'), 0, 'attachment');
    }
    return columns;
  });

  isDocMergeEnabled = false;
  @ViewChild(MatSort) sort: MatSort;
  @ViewChild('searchInput') searchInput!: ElementRef;
  @ViewChild('clearSearch', { read: ElementRef }) clearSearch!: ElementRef;

  protected readonly Workflow = Workflow;
  protected readonly Namespace = Namespace;

  constructor(
    private workflowConfigDataService: WorkflowConfigDataService,
    private workflowConfigService: WorkflowConfigService,
    private messageNotificationService: MessageNotificationService,
    private dialog: MatDialog,
    private uiService: UiService,
    private identityStorage: IdentityStorage,
    private entityTypeService: EntityTypeService,
    private entityTypeServiceFactory: EntityTypeServiceFactory,
    private serviceProviderFactory: SharedServiceProviderServiceFactory,
    private workflowConfigCopyService: WorkflowConfigCopyService,
    private clientConfigService: ClientConfigService,
    private featureFlagService: FeatureFlagService,
  ) {
    effect(() => this.tableFilter());
  }

  ngOnInit(): void {
    this.iconService = this.serviceProviderFactory.getServiceProviderService(this.namespace).getIconService();

    this.clientConfigService.isDocMergeEnabled().subscribe((enabled) => {
      // KAD2-2683 - allow docmerge for Bullhorn even if disabled.
      const isBullhorn = this.namespace === Namespace.bullhorn;
      this.isDocMergeEnabled = enabled || isBullhorn;
    });

    this.load();
    this.workflowConfigService.workflowConfigUpdate$.subscribe(async (workflowConfig) => {
      await this.updateDatasource(workflowConfig);
      this.updateTemplateStats();
    });
    this.recordTerm = this.entityTypeServiceFactory.getRecordTerm(this.namespace);
  }

  // gets hit a lot!
  listIsLoading(): boolean {
    return this.uiService.isLoading();
  }

  createNewTemplate() {
    const workflowConfig = new WorkflowConfig(this.namespace, this.workflowType);
    this.openWorkflowConfigDialog(workflowConfig);
  }

  openWorkflowConfigDialog(workflowConfig: IWorkflowConfig = null): MatDialogRef<WorkflowConfigComponent> {
    return this.dialog.open(WorkflowConfigComponent, {
      width: '95vw',
      maxWidth: '1600px',
      maxHeight: '93vh',
      autoFocus: true,
      data: workflowConfig,
      disableClose: true,
      panelClass: this.namespace + '-namespace',
    });
  }

  openAdv2ConfigDialog(): void {
    this.dialog.open(Adv2ConfigComponent, {
      minWidth: '50vw',
      minHeight: '20vw',
      maxHeight: '83vh',
      autoFocus: true,
      panelClass: this.namespace + '-namespace',
      data: {
        namespace: this.namespace,
      },
    });
  }

  async copyWorkflowConfig(workflowConfig: IWorkflowConfig): Promise<void> {
    const copy = await this.workflowConfigCopyService.copy(workflowConfig);
    this.workflowConfigService.save(copy).subscribe({
      next: (wfc) => {
        this.messageNotificationService.success(`A copy of '${workflowConfig.name}' has been saved.`);
        this.openWorkflowConfigDialog(wfc);
      },
      error: (err) => {
        this.messageNotificationService.error(`There was a problem copying '${workflowConfig.name}'.`, err);
      },
    });
  }

  selectAwesomeDocsTab() {
    this.workflowType = Workflow.SimpleDocx;
    this.setWorkflowFilter(Workflow.SimpleDocx);
  }

  selectDocMergeTab() {
    this.workflowType = Workflow.DocumentMerge;
    this.setWorkflowFilter(Workflow.DocumentMerge);
  }

  filterColumnData(event: any) {
    this.columnFilter.set(this.createColumnFilter(event));
  }

  filterEntities(filterValue: string): void {
    this.clearInput(this.searchInput.nativeElement);
    this.entityFilter.set(filterValue);
  }

  clearInput(inputElement: HTMLElement, focusInput?: boolean): void {
    if (this.dialog.openDialogs.length === 0) {
      this.columnFilter.set([]);
      this.entityFilter.set('');

      const input = inputElement as HTMLInputElement;
      input.value = '';
      if (focusInput) {
        inputElement.focus();
      }
    }
  }

  templateHasAttachmentSection(template: IWorkflowConfig): boolean {
    return template.sections.some((section: Section) => section.type === SectionType.File);
  }

  private load(): void {
    this.uiService.load();

    const configs$ = this.workflowConfigDataService.get();
    const labels$ = this.entityTypeService.getEntitiesForNamespace(this.namespace).pipe(
      catchError((err) => {
        console.error('Failed to load entity labels.', err);
        return of<IEntityLabelPair[]>([]);
      }),
    );

    combineLatest([configs$, labels$]).subscribe({
      next: ([configs, labels]) => {
        this.uiService.loaded();
        this.entityLabelPairList = labels;
        this.populateDatasource(configs);
      },
      error: (error) => {
        this.uiService.loaded();
        this.messageNotificationService.error('Failed to load data.', error);
      },
    });
  }

  private updateTemplateStats(): void {
    this.calculateWorkflowCounts();
    this.countTemplateFilters();
  }

  private calculateWorkflowCounts(): void {
    this.awesomeDocsCount = this.dataSource?.data.filter((wfc) => wfc.workflow === Workflow.SimpleDocx)?.length;
    this.docMergeCount = this.dataSource?.data.filter((wfc) => wfc.workflow === Workflow.DocumentMerge)?.length;
  }

  private countTemplateFilters() {
    const workflowData = this.dataSource?.data?.filter((wfc) => wfc.workflow === this.workflowType);
    if (!workflowData) {
      return;
    }

    const groupedByEntity = workflowData?.reduce((accumulator: any, currentValue: any) => {
      (accumulator[currentValue['entity']] = accumulator[currentValue['entity']] || []).push(currentValue);
      return accumulator;
    }, {});

    this.entityTypesUsed = [];
    Object.keys(groupedByEntity)?.forEach((entityName) => {
      const configs: WorkflowConfig[] = groupedByEntity[entityName];
      const { label: entityLabel, objectName: entityTooltip, iconUrl, iconColor } = this.getLabelForEntity(entityName);
      this.entityTypesUsed.push({
        entity: entityName,
        label: entityLabel,
        tooltip: entityTooltip,
        count: configs.length,
        iconUrl,
        iconColor,
      });
    });

    this.sortEntityTypesUsed();
  }

  private populateDatasource(workflowConfigs: IWorkflowConfig[]): void {
    this.workflowConfigs = workflowConfigs;
    this.totalTemplates = workflowConfigs.length;
    const filtered = workflowConfigs.filter((wfc) => this.isEntityAllowed(wfc.entity));
    const tableViewModel = filtered.map((wfc) => {
      const { label: label, objectName: tooltip } = this.getLabelForEntity(wfc.entity);
      return { label: label, tooltip: tooltip, ...wfc };
    });
    this.dataSource = new MatTableDataSource<TableViewModel>(tableViewModel);
    this.dataSource.sortingDataAccessor = (data, sortHeaderId) => {
      const value = data[sortHeaderId];
      return typeof value === 'string' ? value.toUpperCase() : value;
    };
    this.dataSource.sort = this.sort;
    this.datasourceInitiated.set(true);
  }

  private sortEntityTypesUsed() {
    this.entityTypesUsed.sort((a, b) => a.label.localeCompare(b.label));
  }

  private async updateDatasource(event: WorkflowConfigUpdateEvent): Promise<void> {
    const { entityLabel, entityTooltip } = await this.fetchEntityLabels(event);
    const entityAllowed = this.isEntityAllowed(event.workflowConfig.entity);

    if (event.hint === WorkflowConfigUpdateHint.CREATED) {
      if (entityAllowed) {
        this.dataSource.data.push({
          label: entityLabel,
          tooltip: entityTooltip,
          ...event.workflowConfig,
        });
      }
    } else if (event.hint === WorkflowConfigUpdateHint.UPDATED) {
      const index = this.dataSource.data.findIndex((wfc) => wfc.id === event.workflowConfig.id);
      if (index !== -1) {
        if (entityAllowed) {
          this.dataSource.data.splice(index, 1, {
            label: entityLabel,
            tooltip: entityTooltip,
            ...event.workflowConfig,
          });
        } else {
          this.dataSource.data.splice(index, 1);
        }
      } else if (entityAllowed) {
        this.dataSource.data.push({
          label: entityLabel,
          tooltip: entityTooltip,
          ...event.workflowConfig,
        });
      }
    } else if (event.hint === WorkflowConfigUpdateHint.DELETED) {
      const index = this.dataSource.data.findIndex((wfc) => wfc.id === event.workflowConfig.id);
      if (index !== -1) {
        this.dataSource.data.splice(index, 1);
      }
    }

    this.dataSource.sort = this.sort;
  }

  private async fetchEntityLabels(event: WorkflowConfigUpdateEvent): Promise<{ entityLabel: string; entityTooltip: string }> {
    // since the ReplaySubject won't complete, we need to take 1 so the promise resolves
    const entityLabelPairList$ = this.entityTypeService.getEntitiesForNamespace(event.workflowConfig.clientNamespace).pipe(take(1));
    const entityLabels = await entityLabelPairList$.toPromise();
    const { label: entityLabel, objectName: entityTooltip } = this.getLabelForEntity(event.workflowConfig.entity);
    return { entityLabel, entityTooltip };
  }
  // @TODO: seprate salesforce from other datasources?
  private getLabelForEntity(entityName: string): { label: string; objectName: string; iconUrl?: string; iconColor?: string } {
    const objectName = this.entityTypeServiceFactory.lookupEntityName(this.namespace, entityName);
    const entityMeta = this.entityLabelPairList.find((elp) => elp.objectName === objectName);
    const label = entityMeta?.label ?? entityName;
    return { label, objectName, iconUrl: entityMeta?.iconUrl, iconColor: entityMeta?.iconColor };
  }

  private isEntityAllowed(entityName: string): boolean {
    if (this.namespace !== Namespace.salesforce) {
      return true;
    }
    const objectName = this.entityTypeServiceFactory.lookupEntityName(this.namespace, entityName);
    return this.entityLabelPairList.some((elp) => elp.objectName === objectName);
  }

  private setWorkflowFilter(workflowType: string): void {
    this.workflowFilter.set(workflowType);
    this.clearInput(this.searchInput.nativeElement, false);
  }

  private tableFilter(): void {
    if (!this.datasourceInitiated()) {
      return;
    }
    this.dataSource.filterPredicate = (workflowConfig: any, filterString: string) => this.filterData(workflowConfig, filterString);
    this.dataSource.filter = JSON.stringify(this.combinedTableFilter());
    this.noResults = this.dataSource.filteredData.length === 0;
    this.updateTemplateStats();
  }

  private filterData(workflowConfig: IWorkflowConfig, filterString: string): boolean {
    const tableFiltersObj: ICombinedFilter = JSON.parse(filterString);
    const workflowTypeMatch: boolean = tableFiltersObj.workflowType?.value === workflowConfig.workflow;

    let entityTypeMatch = true;
    if (tableFiltersObj.entityFilter?.value) {
      entityTypeMatch = workflowConfig.entity?.toString()?.toLowerCase() === tableFiltersObj.entityFilter?.value?.toLowerCase();
    }

    let allColumnFiltersMatch = true;
    if (tableFiltersObj.columnFilter?.length) {
      allColumnFiltersMatch = tableFiltersObj.columnFilter?.some((filter) => {
        return filter === null ? true : workflowConfig[filter.column]?.toString().toLowerCase().includes(filter.value.toLowerCase());
      });
    }

    return workflowTypeMatch && entityTypeMatch && allColumnFiltersMatch;
  }

  private createColumnFilter(event: any): ITableFilter[] {
    const filterValue = (event.target as HTMLInputElement).value;
    const labelFilter: ITableFilter = { column: 'label', value: filterValue };
    const nameFilter: ITableFilter = { column: 'name', value: filterValue };
    const createdByFilter: ITableFilter = { column: 'createdBy', value: filterValue };
    const modifiedByFilter: ITableFilter = { column: 'modifiedBy', value: filterValue };
    return [labelFilter, nameFilter, createdByFilter, modifiedByFilter];
  }

  // Keyboard Shortcuts - Use Alt + N to add a new template
  //                    - Use Ctrl + Shift + F to focus the search input
  //                    - Use Escape to clear the search input
  @HostListener('document:keydown', ['$event'])
  protected handleKeydownEvent(event: KeyboardEvent) {
    const searchInput = this.searchInput?.nativeElement;
    const clearSearch = this.clearSearch?.nativeElement;

    if (event.code === 'Escape' && clearSearch && searchInput === document.activeElement) {
      clearSearch.click();
    }

    if (this.dialog.openDialogs.length === 0 && event.ctrlKey && event.shiftKey) {
      if (event.code === 'KeyF') {
        searchInput.focus();
      }
    }

    if (event.altKey && event.code === 'KeyN') {
      if (this.dialog.openDialogs.length === 0) {
        this.createNewTemplate();
      }
    }
  }
}
